// The one lifecycle budget (sdk ADR-012).
export const LIFECYCLE_BUDGET_MS = 10_000;

const EXPIRED = Symbol('lifecycle-budget-expired');

export type BudgetedOutcome<T> =
  | { kind: 'settled'; value: T; elapsedMs: number }
  | { kind: 'failed'; cause: unknown; elapsedMs: number }
  | { kind: 'expired'; elapsedMs: number };

/** Await `work` for at most `budgetMs`. Neither an expiry nor a rejection
 *  rejects — both RESOLVE, distinguishably, and the caller decides
 *  (sdk ADR-018). */
export async function withBudget<T>(
  work: Promise<T>,
  budgetMs: number = LIFECYCLE_BUDGET_MS,
): Promise<BudgetedOutcome<T>> {
  const startedAt = Date.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<typeof EXPIRED>((resolve) => {
    timer = setTimeout(() => resolve(EXPIRED), budgetMs);
  });
  // The rejection becomes the reported `failed` outcome, so this settles the
  // abandoned round trip without dropping its cause (no-fallbacks.md).
  const observed = work.then(
    (value) => ({ ok: true, value }) as const,
    (cause: unknown) => ({ ok: false, cause }) as const,
  );
  try {
    const raced = await Promise.race<
      { readonly ok: true; readonly value: T } | { readonly ok: false; readonly cause: unknown } | typeof EXPIRED
    >([observed, expiry]);
    const elapsedMs = Date.now() - startedAt;
    if (raced === EXPIRED) {
      return { kind: 'expired', elapsedMs };
    }
    if (raced.ok) {
      return { kind: 'settled', value: raced.value, elapsedMs };
    }
    return { kind: 'failed', cause: raced.cause, elapsedMs };
  } finally {
    clearTimeout(timer);
  }
}
